import { Check, SquareDashedMousePointer, Tag, X } from "lucide-react";
import { useEffect, useState } from "react";

import type { Instance, ReviewCreate, TakeoffConfiguration } from "@/api/client";
import { feetInches } from "@/flow/takeoff";
import { STATE_LABEL, reviewState } from "@/flow/decisions";
import { Badge } from "@/ui/Badge";
import type { Tone } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { cn } from "@/ui/cn";
import { unitOf } from "@/viewer/PanelOverlay";

interface Props {
  instance: Instance;
  configurations: readonly TakeoffConfiguration[];
  onDecide: (instanceId: string, body: ReviewCreate) => Promise<boolean>;
  /** Opens the rename dialog for this panel. */
  onRelabel: () => void;
  /** Starts boxing a block of this panel's configuration on the sheet, to relabel together. */
  onBoxBlock?: () => void;
  onClose: () => void;
  error?: string | null;
  /** A role that may not review sees the panel and its decisions, not the buttons. */
  readOnly?: boolean;
}

type Mode = "idle" | "reject";

export const STATE_TONE: Record<ReturnType<typeof reviewState>, Tone> = {
  proposed: "warn",
  accepted: "ok",
  rejected: "danger",
  relabelled: "violet",
};

const FIELD =
  "h-8 w-full rounded-md border border-line-strong bg-surface px-2.5 text-sm text-ink placeholder:text-muted focus:border-accent focus:outline-none";

/**
 * The panel the estimator clicked: what it is, and the three things they can say about
 * it. Every decision is a new record; none replaces an earlier one.
 */
export function PanelCard({
  instance,
  configurations,
  onDecide,
  onRelabel,
  onBoxBlock,
  onClose,
  error = null,
  readOnly = false,
}: Props) {
  const [mode, setMode] = useState<Mode>("idle");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setMode("idle");
    setReason("");
  }, [instance.id]);

  const unitId = unitOf(instance);
  const configuration = configurations.find((c) => c.unit_id === unitId);
  const state = reviewState(instance);

  const decide = async (body: ReviewCreate) => {
    setBusy(true);
    const ok = await onDecide(instance.id, body);
    setBusy(false);
    if (ok) {
      setMode("idle");
    }
  };

  const decisionButton = (on: boolean) =>
    cn("px-2", on && "ring-2 ring-offset-1 ring-offset-surface");

  return (
    <section aria-label="Selected panel" className="flex flex-col gap-4 p-4">
      <header className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="mt-1 size-4 shrink-0 rounded"
          style={{ background: configuration?.colour ?? "var(--color-muted)" }}
        />
        <div className="min-w-0 flex-1">
          <h2 className="truncate font-mono text-[15px] font-semibold text-ink">
            {configuration?.mark ?? "Panel"}
          </h2>
          {configuration?.summary ? (
            <p className="text-[13px] text-muted">{configuration.summary}</p>
          ) : null}
        </div>
        <Badge tone={STATE_TONE[state]} dot>
          {STATE_LABEL[state]}
        </Badge>
        <Button
          variant="ghost"
          size="sm"
          className="-mt-0.5 -mr-1 w-7 px-0"
          onClick={onClose}
          aria-label="Close panel details"
        >
          <X aria-hidden="true" />
        </Button>
      </header>

      <dl className="grid grid-cols-2 gap-2 text-sm">
        <Fact label="Size">
          {instance.width_ft === null && instance.height_ft === null
            ? "not measured"
            : `${feetInches(instance.width_ft)} × ${feetInches(instance.height_ft)}`}
        </Fact>
        <Fact label="Floor">{instance.floor ?? "not read"}</Fact>
        <Fact label="Bay">{instance.bay ?? "not read"}</Fact>
        <Fact label="Elevation">{instance.elevation ?? instance.sheet_number ?? "not read"}</Fact>
      </dl>

      {state !== "proposed" && instance.latest_decision?.reviewer ? (
        <p className="text-xs text-muted">
          {STATE_LABEL[state]} by {instance.latest_decision.reviewer},{" "}
          {new Date(instance.latest_decision.created_at).toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
          })}
        </p>
      ) : null}

      {state === "rejected" && instance.latest_decision?.comment ? (
        <p className="rounded-md bg-danger-soft px-3 py-2 text-[13px] text-danger">
          Rejected: {instance.latest_decision.comment}
        </p>
      ) : null}

      {mode === "idle" && !readOnly ? (
        <div className="grid grid-cols-3 gap-2" role="group" aria-label="Decision">
          <Button
            variant={state === "accepted" ? "primary" : "secondary"}
            className={decisionButton(state === "accepted")}
            disabled={busy}
            aria-pressed={state === "accepted"}
            onClick={() => void decide({ decision: "accept" })}
          >
            <Check aria-hidden="true" />
            Accept
          </Button>
          <Button
            className={decisionButton(state === "rejected")}
            disabled={busy}
            aria-pressed={state === "rejected"}
            onClick={() => setMode("reject")}
          >
            <X aria-hidden="true" />
            Reject
          </Button>
          <Button
            className={decisionButton(state === "relabelled")}
            disabled={busy}
            aria-pressed={state === "relabelled"}
            onClick={onRelabel}
          >
            <Tag aria-hidden="true" />
            Relabel
          </Button>
        </div>
      ) : null}
      {mode === "idle" && onBoxBlock && !readOnly ? (
        <Button size="sm" variant="ghost" className="self-start" onClick={onBoxBlock}>
          <SquareDashedMousePointer aria-hidden="true" />
          Box its block to relabel together
        </Button>
      ) : null}

      {mode === "reject" ? (
        <form
          className="flex flex-col gap-3 rounded-lg border border-line bg-sunken p-3"
          onSubmit={(event) => {
            event.preventDefault();
            void decide({ decision: "reject", comment: reason.trim() || null });
          }}
        >
          <label className="flex flex-col gap-1 text-[13px] font-medium text-ink-2">
            <span>Why it is not a panel</span>
            <input
              type="text"
              className={FIELD}
              value={reason}
              autoFocus
              placeholder="Optional"
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
          <div className="flex gap-2">
            <Button type="submit" variant="danger" size="sm" disabled={busy}>
              Reject panel
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setMode("idle")}>
              Cancel
            </Button>
          </div>
        </form>
      ) : null}

      {error ? (
        <p className="text-[13px] text-danger" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg bg-sunken px-3 py-2">
      <dt className="text-[11px] font-medium tracking-wide text-muted uppercase">{label}</dt>
      <dd className="tabular mt-0.5 truncate font-medium text-ink">{children}</dd>
    </div>
  );
}
